import type { UserProfile, OrganisationProfile, OrgMember } from "@/lib/types"

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.kanu.energy"

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

function parseJwtExpiry(token: string): Date | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]))
    return payload.exp ? new Date(payload.exp * 1000) : null
  } catch {
    return null
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers ?? {}),
  }

  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers })
  const json = await res.json().catch(() => ({}))

  if (!res.ok) {
    throw new ApiError(res.status, json?.message ?? "Something went wrong")
  }

  return json as T
}

export const api = {
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  get: <T>(path: string) => request<T>(path),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
}

export function setUserProfile(profile: UserProfile) {
  localStorage.setItem("userProfile", JSON.stringify(profile))
}

export function getUserProfile(): UserProfile | null {
  const raw = localStorage.getItem("userProfile")
  if (!raw) return null
  try {
    return JSON.parse(raw) as UserProfile
  } catch {
    return null
  }
}

export function clearUserProfile() {
  localStorage.removeItem("userProfile")
}

export function setOrganizationsProfile(orgs: OrganisationProfile[]) {
  localStorage.setItem("organizationsProfile", JSON.stringify(orgs))
}

export function getOrganizationsProfile(): OrganisationProfile[] | null {
  const raw = localStorage.getItem("organizationsProfile")
  if (!raw) return null
  try {
    return JSON.parse(raw) as OrganisationProfile[]
  } catch {
    return null
  }
}

export function clearOrganizationProfile() {
  localStorage.removeItem("organizationsProfile")
}

export function setToken(token: string) {
  const expires = parseJwtExpiry(token)
  const expireStr = expires ? `; expires=${expires.toUTCString()}` : ""
  document.cookie = `token=${encodeURIComponent(token)}; path=/${expireStr}; SameSite=Strict`
}

export function getToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)token=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

export function clearToken() {
  document.cookie = "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict"
}

export const userApi = {
  getUser: (id: string) =>
    api.get<{ data: UserProfile }>(`/users/${id}`),

  updateUser: (id: string, data: Partial<Pick<UserProfile, "first_name" | "last_name" | "photo_url">>) =>
    api.put<{ data: unknown }>(`/users/${id}`, data),

  changePassword: (id: string, data: { old_password: string; new_password: string; confirm_password: string }) =>
    api.put<{ data: unknown }>(`/users/change-password/${id}`, data),

  getMembers: () =>
    api.get<{ data: OrgMember[]; pagination: unknown }>("/users/"),

  inviteMember: (email: string) =>
    api.post<{ data: unknown }>("/auth/invite", { email }),

  suspendMember: (id: string) =>
    api.patch<{ data: unknown }>(`/users/${id}/suspend`, {}),

  restoreMember: (id: string) =>
    api.patch<{ data: unknown }>(`/users/${id}/restore`, {}),

  makeAdmin: (id: string) =>
    api.patch<{ data: unknown }>(`/users/${id}/make-admin`, {}),

  revokeAdmin: (id: string) =>
    api.patch<{ data: unknown }>(`/users/${id}/revoke-admin`, {}),

  removeMember: (id: string) =>
    api.delete<{ data: unknown }>(`/users/${id}`),
}

export const orgApi = {
  getOrganisation: (id: string) =>
    api.get<{ data: OrganisationProfile }>(`/organisations/${id}`),

  updateOrganisation: (id: string, data: Partial<OrganisationProfile>) =>
    api.put<{ data: unknown }>(`/organisations/${id}`, data),
}

export interface Substation {
  id: string
  created_at: string
  updated_at: string
  name: string
  normalizedName: string
  type: string | null
  voltageKv: string | null
  latitude: string | null
  longitude: string | null
  dno: string | null
  gspParent: string | null
  region: string | null
  dataQualityFlag: string | null
  constraintStatus: string | null
  headroomMw: string | null
  firmCapacityMva: string | null
  rawStatusLabel: string | null
  dataDate: string | null
  source: string | null
}

export interface ProjectGridConnection {
  id: string
  created_at: string
  updated_at: string
  distanceKm: string
  calculatedAt: string | null
  feasibilityScore: string | null
  rank: number
  substation: Substation
}

export const substationApi = {
  syncProjectConnections: (projectId: string) =>
    api.post<{ data: ProjectGridConnection[] }>(
      `/project-substation-connections/sync/${projectId}`,
      {},
    ),
}

export const authApi = {
  verifyEmail: (token: string) =>
    api.post<{ data: unknown }>("/auth/verify-email", { token }),

  resendVerification: (email: string) =>
    api.post<{ data: unknown }>("/auth/resend-verification", { email }),
}
